import React, { useState, useEffect } from 'react';
import './CommonMaster.css';

// --- Helper to get view and ID from URL ---
const getRouteInfo = () => {
    const path = window.location.pathname;
    if (path.includes('/common-master/edit/')) {
        const id = path.split('/').pop();
        return { view: 'form', id };
    }
    if (path.includes('/common-master/new')) {
        return { view: 'form', id: null };
    }
    return { view: 'list', id: null };
};

// --- Placeholders for form fields for better user experience ---
const placeholders = {
    docType: "e.g., IN",
    priorityInDailyTask: "e.g., High, Medium, Low",
    category: "e.g., Deployment",
    currentStatus: "e.g., Follow-up",
    bankName: "e.g., Central Bank",
    monthName: "e.g., Feb",
    financialYear: "e.g., 2025-2026",
    attachmentSize: "e.g., 1024",
    unit: "e.g., Kgs",
    vendorType: "e.g., Supplier",
    sector: "e.g., Technology",
    assetType: "e.g., Vehicle",
    assetMake: "e.g., Toyota",
    alertEmailId: "e.g., alerts@example.com",
    alertIdPwd: "Enter password",
    alertIdSmtp: "e.g., smtp.example.com",
    alertIdPort: "e.g., 587",
    alertSslType: "e.g., TLS",
    attachmentFileType: "e.g., PDF, JPG",
    yearAbbr: "e.g., 2526",
    currency: "e.g., USD",
    revType: "e.g., OT",
    componentFor: "e.g., Crane",
    invoiceFor: "e.g., Demob",
    encl: "e.g., Completion Certificate",
};

const CommonMaster = () => {
    const [route, setRoute] = useState(getRouteInfo());
    const [masters, setMasters] = useState([]);
    const [columns, setColumns] = useState([]);
    const [formData, setFormData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentColumn, setCurrentColumn] = useState(null);
    const [newValue, setNewValue] = useState('');

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

    // --- Effect for fetching data based on the current view ---
    useEffect(() => {
        const fetchColumnsAndMasters = async () => {
            setIsLoading(true);
            try {
                if (!API_BASE_URL) throw new Error("API base URL is not defined.");

                // Fetch dynamic columns
                const columnsResponse = await fetch(`${API_BASE_URL}/api/get-common-master-columns`);
                if (!columnsResponse.ok) throw new Error('Failed to fetch columns');
                const columnsData = await columnsResponse.json();
                setColumns(columnsData.columns || []);

                // Fetch master data list
                const mastersResponse = await fetch(`${API_BASE_URL}/api/get-common-master`);
                if (!mastersResponse.ok) throw new Error('Failed to fetch common masters');
                const mastersData = await mastersResponse.json();
                setMasters((mastersData.message || []).sort((a, b) => a.id - b.id));

            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        const fetchMasterById = async (id) => {
            setIsLoading(true);
            try {
                if (!API_BASE_URL) throw new Error("API base URL is not defined.");
                const response = await fetch(`${API_BASE_URL}/api/get-common-master-by-id?id=${id}`);
                if (!response.ok) throw new Error(`Failed to fetch master with ID ${id}`);
                const data = await response.json();
                setFormData(data.message);
            } catch (error) {
                console.error('Error fetching master by ID:', error);
            } finally {
                setIsLoading(false);
            }
        };
        
        const initializeNewMaster = () => {
            setFormData({
                docType: '', priorityInDailyTask: '', category: '', currentStatus: '', bankName: '',
                monthName: '', financialYear: '', attachmentSize: 0, unit: '', vendorType: '',
                sector: '', assetType: '', assetMake: '', alertEmailId: '', alertIdPwd: '',
                alertIdSmtp: '', alertIdPort: 0, alertSslType: '', attachmentFileType: '',
                fromDate: '', toDate: '', yearAbbr: '', active: true, currency: '', revType: '',
                componentFor: '', invoiceFor: '', encl: '',
            });
        };

        if (route.view === 'list') {
            fetchColumnsAndMasters();
        } else if (route.view === 'form' && route.id) {
            fetchMasterById(route.id);
        } else {
            initializeNewMaster();
        }
    }, [route, API_BASE_URL]);

    // --- Navigation handlers ---
    const navigate = (path) => {
        window.history.pushState({}, '', path);
        setRoute(getRouteInfo());
    };
    const handleCreateClick = () => navigate('/common-master/new');
    const handleEditClick = (master) => navigate(`/common-master/edit/${master.id}`);
    const handleCancel = () => navigate('/common-master');

    // --- Modal Handlers for adding a new entry to a column ---
    const handleAddEntryClick = (columnName) => {
        setCurrentColumn(columnName);
        setNewValue('');
        setIsModalOpen(true);
    };

    const handleModalSubmit = async () => {
        if (!currentColumn || !newValue.trim()) {
            alert('Please enter a value.');
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/edit-common-master-column?columnName=${encodeURIComponent(currentColumn)}&value=${encodeURIComponent(newValue)}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
            });

            if (response.ok) {
                alert('New entry added successfully!');
                setIsModalOpen(false);
                // Refetch masters to show the updated list
                const res = await fetch(`${API_BASE_URL}/api/get-common-master`);
                const data = await res.json();
                setMasters((data.message || []).sort((a, b) => a.id - b.id));
            } else {
                const errorData = await response.json();
                alert(`Failed to add entry: ${errorData.message || 'Unknown server error'}`);
            }
        } catch (error) {
            console.error('Error adding new entry:', error);
            alert('An error occurred while adding the new entry.');
        } finally {
            setIsLoading(false);
        }
    };

    // --- Form Handlers ---
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!API_BASE_URL) {
            alert("API configuration is missing.");
            return;
        }

        const isEditing = !!route.id;
        const url = isEditing 
            ? `${API_BASE_URL}/api/edit-common-master-by-id?id=${route.id}`
            : `${API_BASE_URL}/api/edit-common-master`;

        const payload = { ...formData };
        if (payload.fromDate) payload.fromDate = new Date(payload.fromDate).toISOString();
        if (payload.toDate) payload.toDate = new Date(payload.toDate).toISOString();

        try {
            const response = await fetch(url, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                alert(`Common master ${isEditing ? 'updated' : 'created'} successfully!`);
                navigate('/common-master');
            } else {
                const errorData = await response.json();
                alert(`Failed to ${isEditing ? 'update' : 'create'} master: ${errorData.message || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            alert('An error occurred while submitting the form.');
        }
    };

    // --- Helper function for displaying values in the table ---
    const displayValue = (value) => {
        if (value === null || value === undefined || value === '') {
            return <span className="empty-value">—</span>;
        }
        return typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value);
    };

    // --- RENDER LOGIC: Conditionally render list or form ---
    if (route.view === 'list') {
        return (
            <div className="common-master-container">
                <div className="top-bar">
                    <h1>Common Master</h1>
                    <button className="create-btn" onClick={handleCreateClick}>
                        Create / Edit Full Record
                    </button>
                </div>
                {isLoading ? (
                    <div>Loading...</div>
                ) : (
                    <div className="table-responsive">
                        <table className="master-table">
                            <thead>
                                <tr>
                                    <th>Actions</th>
                                    {columns.map(col => (
                                        <th key={col}>
                                            {col.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                            <button className="add-entry-btn" onClick={() => handleAddEntryClick(col)} title={`Add new entry to ${col}`}>+</button>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {masters.map((master) => (
                                    <tr key={master.id}>
                                        <td><button className="edit-btn" onClick={() => handleEditClick(master)}>Edit</button></td>
                                        {columns.map(col => (
                                            <td key={`${master.id}-${col}`}>{displayValue(master[col])}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {isModalOpen && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <h2>Add New Entry for '{currentColumn}'</h2>
                            <input
                                type="text"
                                className="modal-input"
                                value={newValue}
                                onChange={(e) => setNewValue(e.target.value)}
                                placeholder={`Enter new value for ${currentColumn}`}
                                autoFocus
                            />
                            <div className="modal-actions">
                                <button type="button" className="cancel-btn" onClick={() => setIsModalOpen(false)}>Cancel</button>
                                <button type="button" className="save-btn" onClick={handleModalSubmit}>Save</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* --- Floating Action Button --- */}
                
            </div>
        );
    } else {
        return (
            <div className="form-container">
                <div className="form-header">
                    <h1>{route.id ? 'Edit Common Master' : 'Create Common Master'}</h1>
                </div>
                 {isLoading && route.id ? (
                    <div>Loading form data...</div>
                ) : (
                <form onSubmit={handleSubmit} className="full-page-form">
                    <div className="form-grid">
                        {Object.keys(formData).map((key) => {
                            const isDate = key === 'fromDate' || key === 'toDate';
                            const value = isDate && formData[key] ? formData[key].split('T')[0] : formData[key];

                            return (
                                <div className="form-group" key={key}>
                                    <label htmlFor={key}>{key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}</label>
                                    <input
                                        type={isDate ? 'date' : (typeof formData[key] === 'boolean' ? 'checkbox' : 'text')}
                                        id={key}
                                        name={key}
                                        placeholder={placeholders[key] || `Enter ${key}`}
                                        checked={typeof formData[key] === 'boolean' ? formData[key] : undefined}
                                        value={typeof formData[key] !== 'boolean' ? value || '' : ''}
                                        onChange={handleInputChange}
                                        className={typeof formData[key] === 'boolean' ? 'form-checkbox' : 'form-input'}
                                        disabled={key === 'id'}
                                    />
                                </div>
                            );
                        })}
                    </div>
                    <div className="form-footer">
                        <button type="button" className="cancel-btn" onClick={handleCancel}>Cancel</button>
                        <button type="submit" className="save-btn">{route.id ? 'Update' : 'Create'}</button>
                    </div>
                </form>
                 )}
            </div>
        );
    }
};

export default CommonMaster;